import MappingBase from './mappingBase.js';
import Address from './address.js';
import Service from './service.js';
import ServiceCategory from './serviceCategory.js';
import Theme from './theme.js';

class Salon extends MappingBase {
  constructor({
    salonName = null,
    description = null,
    country = 'MX',
    locationNumber = '1',
    franchiseId = null,
    status = 10,
    salonId = null,     // 16-digit canonical ID (Firestore doc ID) — wire alias "sid"
    simpleId = null,    // 5-digit customer-facing short ID — wire alias "siid"
    isMasterF = false,
    address = null,
    phoneNumber = null,
    email = null,
    ownerId = null,
    pStatus = 1,
    pUpdatedAt = null,
    det = null,
    businessHours = null,
    themeId = null,
    // New wizard properties
    businessType = null,
    staffCount = null,
    workingHours = null,
    workingDays = null,
    visualTheme = null, // Complete theme object from JSON
    categories = null, // Service categories object
    services = null, // Services object
    professionals = null, // Professionals array
    locationCoordinates = null,
    // Additional metadata
    slug = null,
    createdAt = null,
    updatedAt = null,
    // Branding images
    primaryLogo = null,
    backgroundImage = null,
    // Detail + list endpoint fields (aliases in shared/fieldMappings.json)
    cancellationPolicy = null,
    paymentMethods = null,
    activeStaffCount = null,
    formattedAddress = null,
    isCurrentlyOpen = null,
    displayPhone = null,
    socialProof = null,
    story = null,
    galleryTitle = null,
    teamTitle = null,
    instagram = null,
    facebook = null,
    currencyConfig = null,
    defaultTaxes = null,
  } = {}) {
    super();
    this.salonName = salonName;
    this.description = description;
    this.country = country;
    this.locationNumber = locationNumber;
    this.franchiseId = franchiseId;
    this.status = status;
    this.salonId = salonId;
    this.simpleId = simpleId;
    this.isMasterF = isMasterF;
    this.address = address ? Address.fromAlias(address) : null;
    this.phoneNumber = phoneNumber;
    this.email = email;
    this.ownerId = ownerId;
    this.pStatus = pStatus;
    this.pUpdatedAt = pUpdatedAt;
    this.det = det;
    this.businessHours = businessHours;
    this.themeId = themeId;
    // Initialize new wizard properties
    this.businessType = businessType;
    this.staffCount = staffCount;
    this.workingHours = workingHours;
    this.workingDays = workingDays;

    // Handle visualTheme. Normalize to the keyed { themeId: Theme } shape
    // that salonThemeManager.applySalonTheme() consumes.
    //
    // The public detail endpoint (SalonPublicDetail) delivers a single flat
    // theme object — the salon's "design style" preset:
    //   { colors: { primary, secondary, background, text, accent },
    //     name, style_name }
    // Older payloads use the keyed shape { themeId: { pc/sc/... } }, so both
    // are tolerated here.
    if (visualTheme && typeof visualTheme === 'object') {
      this.visualTheme = {};

      if (visualTheme.colors && typeof visualTheme.colors === 'object') {
        // Flat "design style" preset from SalonPublicDetail.
        const colors = visualTheme.colors;
        const themeId = visualTheme.style_name || visualTheme.name || 'salon';
        this.visualTheme[themeId] = new Theme({
          id: themeId,
          name: visualTheme.name || null,
          primaryColor: colors.primary || null,
          secondaryColor: colors.secondary || null,
          backgroundColor: colors.background || null,
          textColor: colors.text || null,
          accentColor: colors.accent || null,
        });
      } else {
        // Legacy keyed shape: { themeId: themeData }.
        const themeIds = Object.keys(visualTheme);
        if (themeIds.length > 0) {
          const themeId = themeIds[0];
          const themeData = visualTheme[themeId];

          if (themeData instanceof Theme) {
            this.visualTheme[themeId] = themeData;
          } else if (themeData && typeof themeData === 'object') {
            this.visualTheme[themeId] = Theme.fromAlias({ id: themeId, ...themeData });
          }
        }
      }
    } else {
      this.visualTheme = null;
    }

    // Handle categories - convert from object map to ServiceCategory instances
    if (categories && typeof categories === 'object') {
      this.categories = {};
      Object.entries(categories).forEach(([categoryId, categoryData]) => {
        this.categories[categoryId] = ServiceCategory.fromAlias({ id: categoryId, ...categoryData });
      });
    } else {
      this.categories = categories;
    }

    // Handle services - convert from object map to Service instances
    if (services && typeof services === 'object') {
      this.services = {};
      Object.entries(services).forEach(([serviceId, serviceData]) => {
        this.services[serviceId] = Service.fromAlias({ id: serviceId, ...serviceData });
      });
    } else {
      this.services = services;
    }

    this.professionals = professionals;
    this.locationCoordinates = locationCoordinates;
    // Initialize metadata
    this.slug = slug;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.primaryLogo = primaryLogo;
    this.backgroundImage = backgroundImage;
    // Detail + list endpoint fields
    this.cancellationPolicy = cancellationPolicy;
    this.paymentMethods = paymentMethods;
    this.activeStaffCount = activeStaffCount;
    this.formattedAddress = formattedAddress;
    this.isCurrentlyOpen = isCurrentlyOpen;
    this.displayPhone = displayPhone;
    this.socialProof = socialProof;
    this.story = story;
    this.galleryTitle = galleryTitle;
    this.teamTitle = teamTitle;
    this.instagram = instagram;
    this.facebook = facebook;
    this.currencyConfig = currencyConfig;
    this.defaultTaxes = defaultTaxes;
    console.log('[Salon Constructor] Complete Salon object: ', this);
  }

  static _aliasMapping = {
    snm: 'salonName',
    desc: 'description',
    cc: 'country',
    ln: 'locationNumber',
    fid: 'franchiseId',
    st: 'status',
    sid: 'salonId',    // 16-digit canonical ID (Firestore doc ID)
    siid: 'simpleId',  // 5-digit customer-facing short ID (URL part)
    ismf: 'isMasterF',
    ad: 'address',
    ph: 'phoneNumber',
    em: 'email',
    oid: 'ownerId',
    pst: 'pStatus',
    pdt: 'pUpdatedAt',
    bhrs: 'businessHours',
    thm: 'themeId',
    // Aliases for new wizard properties
    bt: 'businessType',
    sc: 'staffCount',    // wizard write-side alias (SalonCreateRequest)
    wh: 'workingHours',
    wd: 'workingDays',
    vtheme: 'visualTheme',
    cats: 'categories',
    svcs: 'services',
    profs: 'professionals',
    coords: 'locationCoordinates',
    // Aliases for metadata
    slg: 'slug',
    cat: 'createdAt',
    uat: 'updatedAt',
    // Branding images
    plogo: 'primaryLogo',
    bgimg: 'backgroundImage',
    // Detail-endpoint + list-endpoint fields.
    // Canonical aliases from shared/fieldMappings.json in api-salon —
    // all produced by SalonPublicDetail / SalonPublicCard Pydantic
    // serialization_alias declarations.
    astf: 'activeStaffCount',  // computed live count (not wizard string)
    cpol: 'cancellationPolicy',
    curc: 'currencyConfig',
    dph: 'displayPhone',
    dtax: 'defaultTaxes',
    fadr: 'formattedAddress',
    ico: 'isCurrentlyOpen',
    pmth: 'paymentMethods',
    // gtt/sty/cpol also arrive via the edge public_info blob (#50; producer
    // api-salon#436) — same aliases, projection-declared.
    gtt: 'galleryTitle',
    tmt: 'teamTitle',
    sprf: 'socialProof',
    sty: 'story',
    // Salon social links (#56; producer api-salon#443) — footer icons render
    // only when the owner set them. Projection-declared like sty/gtt/tmt.
    igl: 'instagram',
    fbl: 'facebook',
  };

  /**
   * Custom toAlias method that properly transforms nested objects for API
   */
  toAlias() {
    const aliasedData = super.toAlias();

    // Transform visualTheme using Theme.toAlias()
    if (this.visualTheme && typeof this.visualTheme === 'object') {
      const transformedThemes = {};

      Object.entries(this.visualTheme).forEach(([themeId, theme]) => {
        console.log('[Salon.toAlias] Transforming theme with ID:', themeId);
        if (theme && theme.toAlias) {
          console.log('[Salon.toAlias] Original theme data:', theme);
          transformedThemes[themeId] = theme.toAlias();
          console.log('[Salon.toAlias] Transformed theme:', transformedThemes[themeId]);
        }
      });
      aliasedData.vtheme = transformedThemes;
    }

    // Transform categories using ServiceCategory.toAlias()
    if (this.categories && typeof this.categories === 'object') {
      const transformedCategories = {};
      Object.entries(this.categories).forEach(([categoryId, category]) => {
        if (category && category.toAlias) {
          transformedCategories[categoryId] = category.toAlias();
        }
      });
      aliasedData.cats = transformedCategories;
    }

    // Transform services using Service.toAlias()
    if (this.services && typeof this.services === 'object') {
      const transformedServices = {};
      Object.entries(this.services).forEach(([serviceId, service]) => {
        if (service && service.toAlias) {
          transformedServices[serviceId] = service.toAlias();
        }
      });
      aliasedData.svcs = transformedServices;
    }

    return aliasedData;
  }
}

export default Salon;
